import type { AliasedRawBuilder, SelectQueryBuilder, ShallowDehydrateObject, Simplify } from 'kysely'
import { ZodDiscriminatedUnion, type ZodObject } from 'zod'

// ---------------------------------------------------------------------------
// JSON helper types (used by consumers to pass the engine-aware helpers)
// ---------------------------------------------------------------------------

type JsonArrayFromFn = <O>(expr: SelectQueryBuilder<any, any, O>) => {
  as<A extends string>(alias: A): AliasedRawBuilder<Simplify<ShallowDehydrateObject<O>>[], A>
}

type JsonObjectFromFn = <O>(expr: SelectQueryBuilder<any, any, O>) => {
  as<A extends string>(alias: A): AliasedRawBuilder<Simplify<ShallowDehydrateObject<O>> | null, A>
}

export type JsonHelpers = {
  jsonArrayFrom: JsonArrayFromFn
  jsonObjectFrom: JsonObjectFromFn
}

// ---------------------------------------------------------------------------
// resolveFields — reads column names from a Zod schema map at runtime
// ---------------------------------------------------------------------------

/**
 * Returns all field names for a table from a Zod schema map.
 * Use this instead of `selectAll()` inside `jsonArrayFrom`/`jsonObjectFrom`.
 */
export function resolveFields(schema: Record<string, ZodObject | ZodDiscriminatedUnion>, table: string): string[] {
  const s = schema[table]
  if (!s) throw new Error(`[resolveFields] No schema for table '${table}'`)
  if (s instanceof ZodDiscriminatedUnion) {
    return [...new Set(s.def.options.flatMap((o: ZodObject) => Object.keys(o.shape)))]
  }
  return Object.keys(s.shape)
}
